/**
 * Panel de enrolamiento del método OTP por email.
 *
 * Envía un código al correo del usuario (purpose 'enroll') y al verificarlo
 * la callable registra users/{uid}.mfa.method = 'email' y deja la sesión
 * actual con clearance (mfaAuthTime = auth_time), así que no se pide un
 * segundo código inmediatamente después de activar.
 *
 * Se usa tanto en el enrolamiento forzado (enroll/index.js) como en la
 * activación voluntaria (mfa/security_settings.js).
 */

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Form, Input, Button, Typography, notification } from 'antd';
import { SafetyOutlined } from '@ant-design/icons';

import { mfa } from '../../../actions/auth/mfa';
import { MAIL_SENDER } from '../../../lib/mail_sender';

const FormItem = Form.Item;
const { Paragraph, Text } = Typography;

export const MfaEnrollEmail = ({ onEnrolled }) => {

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [form] = Form.useForm();

  const sentRef = useRef(false);

  const sendCode = async () => {
    setSending(true);
    try {
      const res = await dispatch( mfa.sendOtp('enroll') );
      setMaskedEmail(res?.email || null);
      setCooldown(res?.cooldownSeconds || 60);
    } catch ( error ) {
      setCooldown(60);
      notification.info({
        message: 'Revise su correo',
        description: error?.message || 'Ya se envió un código recientemente',
        placement: 'topRight'
      });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    sendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onConfirm = async values => {
    setLoading(true);
    try {
      await dispatch( mfa.verifyOtp( values.code, 'enroll' ) );
      notification.success({
        message: 'Verificación en 2 pasos activada',
        description: 'A partir de ahora recibirá un código en su correo al iniciar sesión',
        placement: 'topRight'
      });
      onEnrolled && onEnrolled();
    } catch ( error ) {
      notification.warning({
        message: 'No se pudo verificar',
        description: error?.message || 'Código incorrecto',
        placement: 'topRight'
      });
      setLoading(false);
    }
  };

  return (
    <div>
      <Paragraph>
        Le llegará un correo con un código de 6 dígitos de parte
        de <Text strong>{ MAIL_SENDER }</Text> a su correo
        registrado{ maskedEmail ? <> <Text strong>{ maskedEmail }</Text></> : null }.
        Si no lo ve, revise la carpeta de spam.
      </Paragraph>
      <Form form = { form } onFinish = { onConfirm } >
        <FormItem name = "code" label = "Código del correo"
          rules = {[
            { required: true, message: 'Debe ingresar el código' },
            { pattern: /^[0-9]{6}$/, message: 'El código tiene 6 dígitos' }
          ]} >
          <Input
            maxLength = { 6 }
            inputMode = "numeric"
            placeholder = "000000"
            style = {{ maxWidth: 200 }}
            prefix = { <SafetyOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> } />
        </FormItem>
        <Button type = "primary" htmlType = "submit" loading = { loading } >
          Activar
        </Button>
        <Button type = "link" disabled = { cooldown > 0 } loading = { sending } onClick = { sendCode } >
          { cooldown > 0 ? `Reenviar código (${cooldown}s)` : 'Reenviar código' }
        </Button>
      </Form>
    </div>
  );
};
